import React, { useState } from "react";
import { Folder, FolderPlus, Star, Archive, LayoutGrid, Pencil, Trash2, Check, X, RotateCcw } from "lucide-react";

const Item = ({ id, active, icon: Icon, label, count, onClick, onDrop, children }) => {
    const [over, setOver] = useState(false);
    return (
        <div data-testid={`folder-item-${id}`} onClick={onClick}
            onDragOver={onDrop ? (e) => { e.preventDefault(); setOver(true); } : undefined}
            onDragLeave={() => setOver(false)}
            onDrop={onDrop ? (e) => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData("text/project-id")); } : undefined}
            className={`group flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm cursor-pointer transition-colors ${active ? "bg-blue-600/20 text-blue-200" : "text-slate-300 hover:bg-[#182232]"} ${over ? "ring-1 ring-blue-400 bg-blue-600/10" : ""}`}>
            <Icon className="w-4 h-4 shrink-0" />
            <span className="flex-1 truncate">{label}</span>
            {children}
            <span className="text-xs font-mono text-slate-500">{count}</span>
        </div>
    );
};

function FolderRow({ f, active, count, onSelect, onDrop, onRename, onDelete, t }) {
    const [edit, setEdit] = useState(false);
    const [val, setVal] = useState(f.name);
    if (edit) {
        const done = () => { if (val.trim()) onRename(f.id, val.trim()); setEdit(false); };
        return (
            <div className="flex items-center gap-1 px-1.5 py-1">
                <input data-testid={`folder-rename-input-${f.id}`} autoFocus value={val} onChange={(e) => setVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") done(); if (e.key === "Escape") setEdit(false); }}
                    className="flex-1 min-w-0 bg-[#0b0f17] border border-[#1e293b] rounded px-2 py-1 text-sm text-slate-100" />
                <button onClick={done} className="p-1 text-emerald-400"><Check className="w-3.5 h-3.5" /></button>
                <button onClick={() => setEdit(false)} className="p-1 text-slate-400"><X className="w-3.5 h-3.5" /></button>
            </div>
        );
    }
    return (
        <Item id={f.id} active={active} icon={Folder} label={f.name} count={count} onClick={onSelect} onDrop={onDrop}>
            <button data-testid={`folder-rename-${f.id}`} title={t("pj_rename")} onClick={(e) => { e.stopPropagation(); setVal(f.name); setEdit(true); }}
                className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 p-0.5 text-slate-400 hover:text-white"><Pencil className="w-3 h-3" /></button>
            <button data-testid={`folder-delete-${f.id}`} title={t("pj_folder_delete")} onClick={(e) => { e.stopPropagation(); if (window.confirm(t("pj_folder_delete_q", { name: f.name }))) onDelete(f.id); }}
                className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 p-0.5 text-slate-400 hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
        </Item>
    );
}

export function FolderSidebar({ folders, view, setView, counts, onCreate, onRename, onDelete, onDropTo, t }) {
    const [name, setName] = useState("");
    const add = () => { if (name.trim()) { onCreate(name.trim()); setName(""); } };
    return (
        <aside data-testid="folder-sidebar" className="w-full lg:w-60 shrink-0 space-y-1">
            <Item id="all" active={view === "all"} icon={LayoutGrid} label={t("pj_all")} count={counts.all} onClick={() => setView("all")} />
            <Item id="favorites" active={view === "favorites"} icon={Star} label={t("pj_favorites")} count={counts.favorites} onClick={() => setView("favorites")} />
            <Item id="archive" active={view === "archive"} icon={Archive} label={t("pj_archive")} count={counts.archive} onClick={() => setView("archive")}
                onDrop={(pid) => onDropTo("archive", pid)} />
            <Item id="trash" active={view === "trash"} icon={RotateCcw} label={t("trash_title")} count={counts.trash ?? ""} onClick={() => setView("trash")} />
            <div className="pt-3 pb-1 px-2.5 text-[11px] uppercase tracking-wider text-slate-500 font-mono">{t("pj_folders")}</div>
            {folders.map((f) => (
                <FolderRow key={f.id} f={f} t={t} active={view === f.id} count={counts[f.id] || 0} onSelect={() => setView(f.id)}
                    onDrop={(pid) => onDropTo(f.id, pid)} onRename={onRename} onDelete={onDelete} />
            ))}
            <div className="flex items-center gap-1 pt-1">
                <input data-testid="new-folder-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
                    placeholder={t("pj_new_folder")} className="flex-1 min-w-0 bg-[#0b0f17] border border-[#1e293b] rounded px-2 py-1.5 text-sm text-slate-100 placeholder:text-slate-600" />
                <button data-testid="new-folder-button" onClick={add} className="p-1.5 rounded border border-[#1e293b] text-slate-300 hover:text-white hover:border-blue-500"><FolderPlus className="w-4 h-4" /></button>
            </div>
        </aside>
    );
}
